import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type SupportCategory = "bug" | "billing" | "feature" | "other";

type SupportDialogProps = {
  trigger: ReactNode;
  onOpenChange?: (open: boolean) => void;
};

/** The support form on its own: posts to /api/web/support, which mails the
 *  monitored inbox. Shared by the navbar dialog and the /contact page so every
 *  "contact us" path stays on this domain. */
export function SupportForm({ defaultCategory = "bug" }: { defaultCategory?: SupportCategory }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [category, setCategory] = useState<SupportCategory>(defaultCategory);
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!message.trim()) {
      setStatus({ type: "error", text: t('support.emptyMessage') });
      return;
    }

    setSubmitting(true);
    setStatus(null);

    try {
      const response = await fetch("/api/web/support", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim() || null,
          category,
          message: message.trim(),
        }),
      });
      const json = await response.json();
      if (!response.ok || json?.error) {
        const errorText = typeof json?.message === "string" ? json.message : t('support.error');
        setStatus({ type: "error", text: errorText });
        return;
      }
      setStatus({ type: "success", text: t('support.success') });
      setMessage("");
    } catch (error) {
      setStatus({ type: "error", text: t('support.unableToSend') });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div className="space-y-2">
        <div className="text-sm font-medium">{t('support.emailLabel')}</div>
        <Input
          type="email"
          placeholder={t('support.emailPlaceholder')}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </div>
      <div className="space-y-2">
        <div className="text-sm font-medium">{t('support.category')}</div>
        <Select value={category} onValueChange={(value) => setCategory(value as SupportCategory)}>
          <SelectTrigger>
            <SelectValue placeholder={t('support.selectCategory')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="bug">{t('support.bug')}</SelectItem>
            <SelectItem value="billing">{t('support.billing')}</SelectItem>
            <SelectItem value="feature">{t('support.feature')}</SelectItem>
            <SelectItem value="other">{t('support.other')}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <div className="text-sm font-medium">{t('support.message')}</div>
        <Textarea
          placeholder={t('support.messagePlaceholder')}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          rows={5}
        />
      </div>
      {status && (
        <div className={status.type === "success" ? "text-sm text-emerald-500" : "text-sm text-red-500"}>
          {status.text}
        </div>
      )}
      <div className="flex justify-end">
        <Button type="submit" disabled={submitting}>
          {submitting ? t('support.sending') : t('support.sendRequest')}
        </Button>
      </div>
    </form>
  );
}

export function SupportDialog({ trigger, onOpenChange }: SupportDialogProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Remount the form on every open so a previous success/error message is gone.
  const [formKey, setFormKey] = useState(0);

  useEffect(() => {
    if (open) setFormKey((k) => k + 1);
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        onOpenChange?.(nextOpen);
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('support.title')}</DialogTitle>
          <DialogDescription>{t('support.description')}</DialogDescription>
        </DialogHeader>
        <SupportForm key={formKey} />
      </DialogContent>
    </Dialog>
  );
}
